import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns, Refunds & Exchanges Policy | Cape Ember Coffee Co.';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = 'Read Cape Ember Coffee Co.\'s returns, refunds and exchanges policy, including eligibility, defective products and South African consumer rights.';
    }
  }, []);

  return (
    <div className="min-h-screen pt-20 md:pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className="overline text-[#A94826] mb-2 block">Legal</span>
        <h1 className="font-heading text-4xl md:text-5xl text-[#2D2622] mb-8">
          Returns, Refunds & Exchanges Policy
        </h1>
        
        <div className="prose prose-lg text-[#5C534C] space-y-8">
          <p className="text-lg">
            <strong>Cape Ember Coffee Co.</strong><br />
            <strong>Effective date:</strong> 01 April 2026
          </p>

          <p>
            At Cape Ember Coffee Co., every bag of coffee represents our commitment to quality, freshness and exceptional customer service.
            We carefully select our coffees, work with trusted South African roasting partners and pack our products to preserve their quality.
            We want you to enjoy every cup. If something goes wrong with your order, we're here to help.
          </p>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">1. Returns Eligibility</h2>
            <p>
              Because coffee is a consumable food product, we cannot ordinarily accept change-of-mind returns of opened or unsealed coffee products where there is no defect or other legal basis for a return.
              For unopened, unused and undamaged coffee products, we offer a voluntary return window of <strong>7 calendar days from delivery</strong>, subject to the conditions below.
            </p>
            <p>To qualify for a voluntary return:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The product must be unopened and in its original sealed packaging.</li>
              <li>The product must be unused and in a resaleable condition.</li>
              <li>Proof of purchase or an order number must be provided.</li>
              <li>The return must be requested within 7 calendar days of delivery.</li>
            </ul>
            <p>This voluntary returns policy does not limit any rights customers have under South African consumer protection legislation.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">2. Damaged, Defective or Incorrect Products</h2>
            <p>
              If your coffee arrives damaged, defective, unsafe, or different from what you ordered, please contact us as soon as possible.
              For faster assistance, we recommend contacting us within <strong>48 hours of delivery</strong>, although this is not a deadline that removes your statutory rights.
            </p>
            <p>Please provide:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your order number</li>
              <li>Your full name and contact details</li>
              <li>A description of the problem</li>
              <li>Clear photographs of the product and packaging, where available</li>
            </ul>
            <p>
              Where a product is defective or does not meet applicable legal quality standards, customers are entitled to the remedies provided under the South African Consumer Protection Act, including the applicable six-month statutory protection period.
              Where legally required, we will arrange collection at our expense and offer the customer the applicable choice of repair, replacement or refund. For consumable coffee products, a replacement or refund will generally be the practical remedy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">3. Change-of-Mind Returns</h2>
            <p>We understand that customers may occasionally change their minds. Unopened, sealed coffee products may be returned within our voluntary 7-day return period, provided the eligibility conditions are met.</p>
            <p>Customers are responsible for the direct return courier costs for voluntary change-of-mind returns. Original delivery charges are not ordinarily refundable for voluntary returns, except where required by law.</p>
            <p>Opened coffee cannot ordinarily be returned solely because of personal taste preferences, roast preference or an incorrect grind option selected by the customer. Nothing in this section excludes any applicable statutory cancellation or return right.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">4. Coffee Freshness and Quality</h2>
            <p>Our coffees are prepared and packed with freshness in mind. To preserve quality, we recommend storing coffee in a cool, dry location, away from direct sunlight and moisture, in a properly sealed container.</p>
            <p>Natural differences in flavour, aroma and intensity may occur between coffee origins, roast profiles and brewing methods. Personal taste differences do not automatically constitute a product defect. However, any genuine quality or safety concern will be investigated fairly.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">5. Exchanges</h2>
            <p>If you receive the wrong coffee blend, bag size or grind option due to an error on our part, we will arrange a suitable replacement at no additional cost.</p>
            <p>If you selected the incorrect product or grind option, an exchange may be considered within 7 calendar days of delivery, provided the product remains unopened and sealed. Any additional courier costs for a voluntary exchange will be communicated before the exchange is processed.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">6. Coffee Bundles and Gift Sets</h2>
            <p>Returns of coffee bundles and gift sets are subject to the same conditions as individual coffee products. For voluntary returns, all items in the bundle must be unopened, unused and in their original packaging.</p>
            <p>If one item in a bundle is damaged, defective or incorrect, please contact us so we can arrange an appropriate remedy without unnecessarily returning unaffected products.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">7. Refund Processing</h2>
            <p>Once a qualifying return has been received and assessed, we will notify you of the outcome. Approved refunds will generally be processed within <strong>7–10 business days</strong> after approval, using the original payment method wherever possible.</p>
            <p>The time taken for funds to appear in your account may depend on your bank or payment provider. Any shorter refund period or additional remedy required by applicable law will take precedence.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">8. Lost or Undelivered Orders</h2>
            <p>If your order does not arrive within the communicated delivery period, please contact us. We will investigate with the relevant courier and work toward an appropriate resolution.</p>
            <p>Where goods cannot be supplied or delivered and the customer is legally entitled to cancel, we will provide the applicable refund in accordance with South African law. Customers will not be required to pay return shipping for goods they never received.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">9. How to Request a Return</h2>
            <p>To request a return, exchange or refund, please contact Cape Ember Coffee Co. Please include your order number and a brief explanation of your request.</p>
            <p>Do not send products back without first contacting us for return instructions and the applicable return address.</p>
            <address className="not-italic space-y-1">
              <p><strong>Email:</strong> <a href="mailto:info@capeembercoffee.co.za" className="text-[#A94826] hover:underline">info@capeembercoffee.co.za</a></p>
              <p><strong>Telephone / WhatsApp:</strong> <a href="https://wa.me/27812505914" className="text-[#A94826] hover:underline">+27 81 250 5914</a></p>
              <p><strong>Website:</strong> <a href="https://capeembercoffee.co.za" className="text-[#A94826] hover:underline">https://capeembercoffee.co.za</a></p>
              <p><strong>Location:</strong> Garden Route, Western Cape, South Africa</p>
            </address>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="mailto:info@capeembercoffee.co.za" className="btn-secondary inline-flex items-center justify-center">Email Us</a>
              <a href="https://wa.me/27812505914" target="_blank" rel="noopener noreferrer" className="whatsapp-btn inline-flex items-center justify-center gap-2 px-6 py-3 font-medium">
                <MessageCircle size={20} />
                WhatsApp Us
              </a>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2D2622] mb-4">10. Consumer Rights</h2>
            <p>This policy is intended to comply with applicable South African consumer protection legislation, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The Consumer Protection Act 68 of 2008</li>
              <li>The Electronic Communications and Transactions Act 25 of 2002</li>
            </ul>
            <p>Certain food and everyday-consumption purchases may be excluded from the general seven-day online cooling-off right under section 42 of the Electronic Communications and Transactions Act. This does not remove applicable rights relating to defective, unsafe, incorrectly supplied or otherwise non-compliant goods.</p>
            <p>Where any provision of this policy conflicts with a mandatory consumer right, the applicable law will prevail.</p>
          </section>

          <p className="border-t border-[#E5DCD0] pt-6">
            <strong>Cape Ember Coffee Co.</strong><br />
            <em>South African landscapes in every cup.</em><br /><br />
            Thank you for supporting our proudly South African coffee brand.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-[#E5DCD0]">
          <Link to="/" className="text-[#A94826] hover:underline">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPage;
